"use client";

import { Plus } from "lucide-react";
import * as React from "react";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

interface PropertyPickerCommandContentProps<T> {
  getOptionKey?: (value: T) => string;
  options: Array<{
    value: T;
    label: string;
    icon?: React.ReactNode;
    displayLabel?: React.ReactNode;
  }>;
  searchFn?: (query: string) => Promise<
    Array<{
      value: T;
      label: string;
      icon?: React.ReactNode;
      displayLabel?: React.ReactNode;
    }>
  >;
  renderItem?: (
    option: { value: T; label: string; icon?: React.ReactNode; displayLabel?: React.ReactNode },
    isSelected: boolean,
  ) => React.ReactNode;
  placeholder?: string;
  onSelect?: (value: T) => void;
  selectedValue?: T | null;
  /** When provided, shows a "Create" option when search has no exact match */
  onCreateOption?: (value: string) => void;
  /**
   * The option set itself is still loading (e.g. a lazily-synced collection).
   * Distinguishes "not loaded yet" from a genuinely empty result, so the empty
   * state reads "Loading…" instead of the misleading "No results found".
   */
  isLoading?: boolean;
}

function DefaultCommandItem({ label, icon }: { label: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <>
      {icon}
      <span className="truncate">{label}</span>
    </>
  );
}

export function PropertyPickerCommandContent<T>({
  options,
  getOptionKey,
  searchFn,
  renderItem,
  placeholder = "Search...",
  onSelect,
  selectedValue,
  onCreateOption,
  isLoading,
}: PropertyPickerCommandContentProps<T>) {
  const [searchQuery, setSearchQuery] = React.useState("");
  const [filteredOptions, setFilteredOptions] = React.useState(options);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    const filterOptions = async () => {
      if (!searchQuery.trim()) {
        setFilteredOptions(options);
        return;
      }

      if (searchFn) {
        setLoading(true);
        try {
          const results = await searchFn(searchQuery);
          setFilteredOptions(results);
        } finally {
          setLoading(false);
        }
      } else {
        const filtered = options.filter((opt) => opt.label.toLowerCase().includes(searchQuery.toLowerCase()));
        setFilteredOptions(filtered);
      }
    };

    const debounce = setTimeout(filterOptions, 150);
    return () => clearTimeout(debounce);
  }, [searchQuery, searchFn, options]);

  const handleSelect = (option: { value: T; label: string }) => {
    onSelect?.(option.value);
    setSearchQuery("");
  };

  const exactMatch = filteredOptions.some((o) => o.label.toLowerCase() === searchQuery.trim().toLowerCase());
  const showCreate = onCreateOption && searchQuery.trim() && !exactMatch;

  return (
    <Command shouldFilter={false}>
      <CommandInput placeholder={placeholder} value={searchQuery} onValueChange={setSearchQuery} />
      <CommandList>
        {loading ? (
          <div className="py-6 text-center text-sm text-muted-foreground">Searching...</div>
        ) : filteredOptions.length === 0 && !showCreate ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            {isLoading ? "Loading…" : "No results found"}
          </div>
        ) : (
          <>
            {filteredOptions.length > 0 && (
              <CommandGroup>
                {filteredOptions.map((option) => (
                  <CommandItem
                    key={getOptionKey ? getOptionKey(option.value) : String(option.value)}
                    value={option.label}
                    onSelect={() => handleSelect(option)}
                    data-checked={option.value === selectedValue ? "" : undefined}
                  >
                    {renderItem ? (
                      renderItem(option, option.value === selectedValue)
                    ) : (
                      <DefaultCommandItem label={option.displayLabel ?? option.label} icon={option.icon} />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {showCreate && (
              <CommandGroup>
                <CommandItem
                  onSelect={() => {
                    const val = searchQuery.trim();
                    onSelect?.(val as unknown as T);
                    onCreateOption(val);
                    setSearchQuery("");
                  }}
                >
                  <Plus className="size-3.5 mr-1.5" />
                  Create &ldquo;{searchQuery.trim()}&rdquo;
                </CommandItem>
              </CommandGroup>
            )}
          </>
        )}
      </CommandList>
    </Command>
  );
}
